import { doc, onSnapshot, type Unsubscribe } from 'firebase/firestore';
import {
  APP_VERSION_CONFIG_COLLECTION,
  AppVersionConfigSchema,
  type AppVersionConfig,
  type AppVersionGatePlatform,
} from '@etiquetachef/types';
import { firestore } from './firebase';

const TAG = 'admin/appVersions';

/** As quatro plataformas com doc de kill switch, na ordem em que a tela mostra. */
export const APP_VERSION_PLATFORMS: readonly AppVersionGatePlatform[] = [
  'ios',
  'android',
  'web',
  'admin',
];

/** Uma linha da tabela: a plataforma e o doc dela (ou `null`). */
export type AppVersionRow = {
  platform: AppVersionGatePlatform;
  /** `null` = o doc ainda não existe (plataforma nunca publicada). */
  config: AppVersionConfig | null;
};

/** Estado da tela do kill switch. */
export type AppVersionsResult =
  | { kind: 'LOADING' }
  | { kind: 'READY'; rows: AppVersionRow[] }
  | { kind: 'ERROR'; error: Error };

/**
 * Observa os quatro docs de `/appConfig` em tempo real.
 *
 * Um listener por plataforma, e não uma query na coleção, porque o doc pode não
 * existir — e "não existe" é uma resposta que a tela precisa mostrar ("nunca
 * publicado"), não uma linha ausente da tabela.
 *
 * Origem: #655 — commit acba8e71e, 2026-09-16 (tela do kill switch de versão).
 *
 * Contrato: emite `READY` só depois que as quatro plataformas responderam;
 * qualquer erro de snapshot vira `ERROR` e silencia os `READY` seguintes até
 * remontar. Devolve um unsubscribe que fecha os quatro listeners.
 *
 * Usado por: `routes/AppVersionGatePage.tsx`.
 *
 * Arquitetura: rule `match /appConfig/{platform}`; quem aperta é a callable
 * do kill switch, nunca escrita direta. Os mesmos docs são lidos pelos
 * clientes (Android, iOS, web, e o próprio admin em `lib/useAppVersionGate.ts`).
 *
 * Dados: `appConfig/{ios|android|web|admin}` — `AppVersionConfigSchema`
 * (`latestBuild`, `minBuild`, `latestVersion`, `updatedAt?`).
 *
 * Falhas conhecidas:
 *  - Doc fora do schema aparece como "não publicado" só com `console.warn`,
 *    sem report na telemetria (#926).
 */
export function subscribeAppVersions(onResult: (r: AppVersionsResult) => void): Unsubscribe {
  const configs = new Map<AppVersionGatePlatform, AppVersionConfig | null>();
  let failed = false;

  /** Emite `READY` só quando as quatro plataformas já responderam. */
  const emit = (): void => {
    if (failed) return;
    if (configs.size < APP_VERSION_PLATFORMS.length) return;
    onResult({
      kind: 'READY',
      rows: APP_VERSION_PLATFORMS.map((platform) => ({
        platform,
        config: configs.get(platform) ?? null,
      })),
    });
  };

  const unsubs = APP_VERSION_PLATFORMS.map((platform) =>
    onSnapshot(
      doc(firestore, APP_VERSION_CONFIG_COLLECTION, platform),
      (snap) => {
        if (!snap.exists()) {
          configs.set(platform, null);
          emit();
          return;
        }
        const parsed = AppVersionConfigSchema.safeParse(snap.data());
        if (!parsed.success) {
          // Doc fora do schema vale como ausente: a tela diz "não publicado" em
          // vez de mostrar números pela metade, e o callable recusa apertar.
          console.warn(`${TAG}: doc ${platform} fora do schema`, parsed.error.issues);
          configs.set(platform, null);
        } else {
          configs.set(platform, parsed.data);
        }
        emit();
      },
      (err: Error) => {
        failed = true;
        console.error(`${TAG}: erro em ${platform}`, err);
        onResult({ kind: 'ERROR', error: err });
      },
    ),
  );

  return () => {
    for (const u of unsubs) u();
  };
}

/**
 * O kill switch está valendo?
 *
 * `minBuild === 0` é o estado de repouso: nenhum build é recusado. Qualquer
 * valor acima disso recusa alguém.
 *
 * Origem: #655 — commit acba8e71e, 2026-09-16. Usado por: `routes/AppVersionGatePage.tsx`.
 */
export function gateIsActive(config: AppVersionConfig | null): boolean {
  return config !== null && config.minBuild > 0;
}
